/**
 * The destination model — canonical keys, route ownership, and active state.
 *
 * Issue #55, epic #51. This file is the SINGLE source of truth for the app's
 * navigation targets. Before it existed the same four menu paths were spelled
 * out in four places (`App.tsx`, `Sidebar.tsx`, `UserMenu.tsx` and
 * a quick-actions card), each with its own idea of who was allowed to see
 * them — which is how a Contributor holding `system_settings:read` ended up
 * with a working System Settings page, a menu entry pointing at it, and no
 * sidebar row: three gates, three answers.
 *
 * Two rules make the ownership table trustworthy:
 *
 *  1. **A route is owned by at most one destination.** A test asserts this
 *     against the live route list in `App.tsx`, which is what keeps the table
 *     honest as routes are added — it fails loudly the day someone adds a
 *     route and forgets this file.
 *  2. **Matching respects segment boundaries.** A bare `startsWith` — what
 *     `Sidebar` used to do — would make `/settings` own `/settingsfoo` and
 *     `/admin/users` own `/admin/users-archive`.
 *
 * `Icon` is declared as a COMPONENT, never as a rendered element. The rail
 * draws it at `small` when collapsed and `medium` when expanded, and the
 * bottom bar draws it at its own size — so the size cannot be baked in here.
 *
 * ONE ADMIN DESTINATION, NOT TWO (issue #92, epic #90)
 * ----------------------------------------------------
 * `users` (`/admin/users`) and `system` (`/admin/settings`) used to be two
 * separate rows for what is, to the user, one surface. Issue #92 splits the
 * admin tab strips into one route per settings page under `/admin/settings/*`,
 * and #94 gives the rail a Console mode that swaps its contents to those pages
 * on any `/admin/*` path. Console mode is only coherent if the admin surface is
 * ONE destination: two rows both matching `/admin/*` means two `aria-current`
 * candidates and an ambiguous active state on every admin route. So the two are
 * replaced by a single `console` destination that owns the whole `/admin`
 * subtree.
 */

import type { SvgIconComponent } from '@mui/icons-material';
import TodayIcon from '@mui/icons-material/Today';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PlaceIcon from '@mui/icons-material/Place';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SportsIcon from '@mui/icons-material/Sports';
import type { SettingsFeatureKey, SettingsFeatures } from './adminSections';
import { isFeatureEnabled } from './adminSections';

export type DestinationKey =
  | 'today'
  | 'train'
  | 'health'
  | 'gyms'
  | 'coach'
  | 'settings'
  | 'console'
  | 'ai';

/**
 * The most destinations that may set `primary`. The phone bottom bar cannot hold
 * more: five labelled tabs do not fit at 360px and `showLabels` depends on it.
 * `destinations.test.ts` asserts the primary count against this, so the ceiling
 * is executable rather than a comment.
 */
export const PRIMARY_DESTINATION_LIMIT = 4;

/**
 * Does `prefix` own `path`? True when the path equals the prefix or continues
 * with a `/`. `'/'` matches only itself — every path starts with it, so the
 * root has to be exact or Home would own the entire app.
 */
export function owns(prefix: string, path: string): boolean {
  if (prefix === '/') return path === '/';
  return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Route prefixes each destination owns. Child routes are covered by their
 * parent prefix (`/admin/settings/users`, `/settings/profile`, …) and do not
 * need their own entries.
 *
 * `console` owns the bare `/admin` rather than `/admin/settings`, even though
 * `/admin/settings` is where it NAVIGATES. The two are different questions:
 * `path` is where the row sends you, `DESTINATION_ROUTES` is what makes the row
 * light up. `/admin/users` still exists as a redirect route (#92) and a
 * bookmark still lands on it for one render — with only `/admin/settings` in
 * this list that render would highlight nothing, and the route-ownership test
 * would fail it as "neither owned nor deliberately unowned".
 */
export const DESTINATION_ROUTES: Record<DestinationKey, readonly string[]> = {
  today: ['/'],
  train: ['/train'],
  health: ['/health'],
  gyms: ['/gyms'],
  // E7.8 (#248). The AI Coach timeline. Its settings live at `/settings/coach`
  // and so belong to `settings`, not here.
  coach: ['/coach'],
  settings: ['/settings'],
  console: ['/admin'],
  // Issue #425, epic #419. The AI Playground. `/ai` only — the per-user AI
  // Keys page lives at `/settings/ai` and so belongs to `settings`, and the
  // admin AI pages at `/admin/settings/ai*` belong to `console`.
  ai: ['/ai'],
};

/**
 * Routes deliberately owned by NO destination.
 *
 * These are reached from outside the authenticated shell entirely — the login
 * flow, the OAuth round trip, the device-activation screen — and most do not
 * even mount `Layout`. **On these routes no destination renders as active, and
 * that is correct rather than a bug.** Exported so a test can assert it
 * explicitly, which is what stops a future contributor from "fixing" it into
 * highlighting something arbitrary.
 */
export const UNOWNED_ROUTES: readonly string[] = [
  '/login',
  '/auth/callback',
  '/activate',
  '/testing/login',
];

/**
 * A navigation destination, fully described for every surface that draws it.
 *
 * `permission` is the API permission that makes the destination REACHABLE, and
 * it is deliberately the same string the corresponding controller enforces —
 * see the comments on each entry. A destination with no `permission` and no
 * `anyPermission` is available to every authenticated user.
 */
export interface Destination {
  key: DestinationKey;
  /** Full label — the expanded rail, the bottom bar, the user menu. */
  label: string;
  /** Shown in the 56px collapsed rail, which will not hold "System Settings". */
  compactLabel: string;
  Icon: SvgIconComponent;
  path: string;
  /** API permission required to reach it; absent means "any authenticated user". */
  permission?: string;
  /**
   * Reachable when the user holds ANY ONE of these permissions.
   *
   * Added by #92 for `console`, which fronts pages from two different
   * controllers: someone with `users:read` alone must reach the Users &
   * Allowlist page, and someone with `system_settings:read` alone must reach
   * the settings pages. Neither may be dropped, and the single-string
   * `permission` field cannot express "or".
   *
   * Widening `permission` to `string | string[]` was the alternative and was
   * rejected: an array there reads as ALL by every convention in this codebase
   * (`hasAllPermissions`), so the same field would have meant "and" at one call
   * site and "or" at another. A separate field names the semantics.
   *
   * The two fields AND together when both are set — `permission` must be held
   * AND at least one of `anyPermission`. `ai` is the destination that sets
   * both (#593): `ai:use` AND `ai_config:read`. `isDestinationVisible` is the
   * only place that has to know.
   */
  anyPermission?: readonly string[];
  /**
   * Render this destination pinned at the FOOT of the navigation rail, below a
   * divider, rather than inline in the destination list (#105).
   *
   * `console` is the only one today, and the flag exists so the rail never has
   * to spell `key === 'console'` in its render. A magic key there would be a
   * second, invisible answer to "what is the admin surface" — the exact
   * split-brain this file's header describes — and it would silently stop
   * being true the day the admin destination is renamed or a second mode is
   * added. Declaring it here keeps ONE place that knows Console is a MODE and
   * not a peer of the library destinations, which is what its position at the
   * foot communicates.
   *
   * RAIL-ONLY, deliberately. The bottom bar has no foot to pin to (it IS the
   * foot) and the user menu is a flat list, so both ignore this flag. They
   * read `primary` instead to decide which of them lists a destination.
   */
  pinned?: boolean;
  /**
   * Shown in the phone bottom bar. At most PRIMARY_DESTINATION_LIMIT destinations
   * may set this (asserted in destinations.test.ts). Destinations without it
   * are reached from the user menu on phones; the rail lists all of them.
   */
  primary?: boolean;
  /**
   * Primary ONLY while the named destination is hidden from this user (E7.8,
   * #248): the bottom bar's fallback occupant of a slot whose owner is gated.
   *
   * `gyms` carries `primaryWhenHidden: 'coach'`. Coach (`ai:use` AND the `ai`
   * feature) holds the fourth tab whenever the user can see it; when they
   * cannot (AI off, or no `ai:use`) Gyms takes the slot back, so the bar never
   * drops to three tabs. The named destination must itself be `primary` (a
   * test asserts it), which is what keeps the pair mutually exclusive and the
   * bar within `PRIMARY_DESTINATION_LIMIT` in every state.
   *
   * Never read this flag directly: `isPrimaryDestination` and
   * `resolvePrimaryDestinations` are the only answers to "is this primary for
   * this user", exactly as `isDestinationVisible` is for visibility.
   */
  primaryWhenHidden?: DestinationKey;
  /**
   * A deployment-wide feature this destination only exists under (#425) — the
   * same `feature` field, and the same fail-closed rule, as a settings card
   * (`SettingsCardDef.feature` in `config/adminSections.tsx`): hidden unless
   * the caller's feature map says it is on. AND-ed with the permission gates.
   */
  feature?: SettingsFeatureKey;
}

/**
 * Is `destination` visible to a user with this `hasPermission` predicate?
 *
 * EVERY surface calls this rather than testing `destination.permission`
 * inline. Three surfaces (rail, bottom bar, user menu) each ran
 * their own `!destination.permission || hasPermission(...)` expression, and
 * every one of them silently ignored `anyPermission` the moment it was added —
 * the `console` row would have appeared for everyone. One function is the same
 * fix this file's header describes for the paths themselves.
 */
export function isDestinationVisible(
  destination: Destination,
  hasPermission: (permission: string) => boolean,
  features: SettingsFeatures = {},
): boolean {
  if (!isFeatureEnabled(destination.feature, features)) return false;
  if (destination.permission && !hasPermission(destination.permission)) return false;
  if (destination.anyPermission && !destination.anyPermission.some(hasPermission)) return false;
  return true;
}

/**
 * The destinations, in navigation order: the product destinations (Today,
 * Train, Health, Gyms, Coach; four of them primary for any one user, see
 * `resolvePrimaryDestinations`), then Settings, Console (pinned) and AI.
 *
 * Declaration order IS navigation order on every surface. The bottom bar shows
 * only the primary destinations (`isPrimaryDestination`) and the user menu only
 * the rest. The rail is the one
 * exception, and only for the tail of the list: it lifts `pinned` destinations
 * out to its foot (#105) while leaving the rest in this order.
 *
 * GATING IS BY PERMISSION, NOT BY ROLE, and the permission is the one the API
 * actually enforces — verified against the controllers rather than assumed:
 *
 *   - `users.controller.ts`           → `users:read`
 *   - `system-settings.controller.ts` → `system_settings:read`
 *
 * `console` is reachable on EITHER of those (see `anyPermission`), because
 * `/admin/settings` fronts pages from both controllers and a user entitled to
 * only one half must still reach the surface. The per-page gates inside
 * `/admin/settings/*` are what decide which cards and routes that user actually
 * gets — `config/adminSections.tsx` declares them, and `App.tsx` wraps each
 * route in the matching `RequirePermission`.
 *
 * That is the same REACHABILITY-vs-CONTENT split this file has always drawn:
 * the Users & Allowlist page gates on `users:read` to be reached, while its
 * Allowlist half gates itself on `allowlist:read` inside the page, because its
 * data comes from `allowlist.controller.ts`.
 *
 * `isAdmin` is no longer a navigation gate anywhere. It still exists (and
 * `AdminOnly` with it) for non-navigation uses, but a role check here is what
 * produced the split-brain described in the file header.
 */
export const DESTINATIONS: readonly Destination[] = [
  { key: 'today', label: 'Today', compactLabel: 'Today', Icon: TodayIcon, path: '/', primary: true },
  {
    key: 'train',
    label: 'Train',
    compactLabel: 'Train',
    Icon: FitnessCenterIcon,
    path: '/train',
    primary: true,
  },
  {
    key: 'health',
    label: 'Health',
    compactLabel: 'Health',
    Icon: MonitorHeartIcon,
    path: '/health',
    primary: true,
  },
  {
    key: 'gyms',
    label: 'Gyms',
    compactLabel: 'Gyms',
    Icon: PlaceIcon,
    path: '/gyms',
    // E7.8 (#248): the fourth tab only while Coach is hidden from the user.
    // Otherwise reached from the rail, the user menu and a link on Train.
    primaryWhenHidden: 'coach',
  },
  {
    // E7.8 (#248), docs/specs/ai-coach.md §2.13. The AI Coach timeline: the
    // fourth primary tab whenever the user can see it. `ai:use` is the string
    // the coach controllers enforce (`PERMISSIONS.AI_USE`), and `feature: 'ai'`
    // hides it while AI is off, where every coach route answers `AI_DISABLED`.
    //
    // DECLARED DIRECTLY AFTER `gyms`, so in either state the bar reads Today,
    // Train, Health, then one of Gyms or Coach.
    key: 'coach',
    label: 'Coach',
    compactLabel: 'Coach',
    Icon: SportsIcon,
    path: '/coach',
    permission: 'ai:use',
    feature: 'ai',
    primary: true,
  },
  {
    key: 'settings',
    label: 'User Settings',
    compactLabel: 'Settings',
    Icon: SettingsIcon,
    path: '/settings',
  },
  {
    key: 'console',
    label: 'Console',
    compactLabel: 'Console',
    Icon: AdminIcon,
    path: '/admin/settings',
    anyPermission: ['system_settings:read', 'users:read'],
    // Pinned at the rail's foot (#105) — a mode, not a third library
    // destination. The permission gate above still runs first: a user who
    // cannot reach Console gets no pinned row AND no stray divider.
    pinned: true,
  },
  {
    // Issue #425, epic #419 — the AI Playground, reached from the user menu on
    // phones (not `primary`). `ai:use` is the literal string the consumer AI
    // controllers enforce (`PERMISSIONS.AI_USE`), and `feature: 'ai'` hides it
    // while AI is switched off, where every call it would make answers
    // `403 AI_DISABLED`.
    //
    // ADMIN-ONLY (#593). The Playground is an OPERATOR tool — for checking
    // that a provider, model and key actually work — not an end-user feature,
    // so it additionally requires `ai_config:read`: the exact string the admin
    // `/api/admin/ai/*` controllers enforce, granted only to `Admin` in the
    // seed. The consumer `/api/ai/*` endpoints deliberately stay on `ai:use`
    // alone, because in-app AI features (the telemetry assistant, a fork's
    // own features) call them on behalf of ordinary users. Both strings are
    // required: `permission` and `anyPermission` AND together.
    //
    // DECLARED AFTER `console`, deliberately. Declaration order is navigation
    // order in the user menu, and appending leaves the existing entries where
    // users learnt them. The rail lifts `console` (pinned) to its foot
    // regardless, so there AI sits after Settings in the library list.
    key: 'ai',
    label: 'AI Playground',
    compactLabel: 'AI',
    Icon: AutoAwesomeIcon,
    path: '/ai',
    permission: 'ai:use',
    anyPermission: ['ai_config:read'],
    feature: 'ai',
  },
];

/**
 * Is `destination` in the phone bottom bar for this user?
 *
 * `primary` destinations are, when visible. A `primaryWhenHidden` destination
 * is, when visible AND the destination it stands in for is not. Every surface
 * asks this rather than reading the flags: the bottom bar shows exactly these,
 * and the user menu lists every visible destination that is not one of them.
 */
export function isPrimaryDestination(
  destination: Destination,
  hasPermission: (permission: string) => boolean,
  features: SettingsFeatures = {},
): boolean {
  if (!isDestinationVisible(destination, hasPermission, features)) return false;
  if (destination.primary) return true;
  if (!destination.primaryWhenHidden) return false;
  const owner = DESTINATIONS.find((d) => d.key === destination.primaryWhenHidden);
  return !owner || !isDestinationVisible(owner, hasPermission, features);
}

/**
 * The bottom bar's destinations for this user, in navigation order (E7.8,
 * #248). Pure: the same permission predicate and feature map always give the
 * same four.
 */
export function resolvePrimaryDestinations(
  hasPermission: (permission: string) => boolean,
  features: SettingsFeatures = {},
): Destination[] {
  return DESTINATIONS.filter((destination) =>
    isPrimaryDestination(destination, hasPermission, features),
  );
}

/**
 * Which destination, if any, owns `pathname`.
 *
 * Longest prefix wins where prefixes overlap. `/admin` is a single prefix
 * today, so nothing under it competes — but the rule is what keeps `/` from
 * winning everything (it is handled by `owns`' exact-match case) and what will
 * keep a future sibling prefix correct without touching this function.
 */
export function resolveActiveDestination(pathname: string): DestinationKey | null {
  let best: { key: DestinationKey; length: number } | null = null;

  for (const [key, prefixes] of Object.entries(DESTINATION_ROUTES) as [
    DestinationKey,
    readonly string[],
  ][]) {
    for (const prefix of prefixes) {
      if (!owns(prefix, pathname)) continue;
      if (!best || prefix.length > best.length) {
        best = { key, length: prefix.length };
      }
    }
  }

  return best?.key ?? null;
}
